import {
  useAuthenticated,
  useDivisionsMenuQuery,
  usePageSectionsQuery,
  usePagesQuery
} from '@gettactic/hooks';
import { useDashboardFilters } from '@/contexts/DashboardStateContext';
import { Box } from '@gettactic/components/src/primitives';
import { PageSection } from '@gettactic/api';
import { LoadingSpinner } from '@gettactic/components/src/loadings/LoadingSpinner';
import { findDivisionPathByOfficeId } from '@gettactic/helpers/src/divisions/findDivisionPathByOfficeId';
import React, { useEffect } from 'react';
import { SiteInfoLayout } from './site-info/SiteInfoLayout';

import { SectionCard } from './site-info/SectionCard';
import { PageView } from './site-info/PageView';
import { PageInfoSkeleton } from './site-info/PageInfoSkeleton';

import { SectionContent } from './SectionContent';
import { PageCardGrid } from './site-info/PageCardGrid';

export const SiteInfo = () => {
  const {
    userContext: { currentOffice }
  } = useAuthenticated();
  const sectionsQuery = usePageSectionsQuery();
  const allSections = sectionsQuery.data || [];
  const sectionsLoading = sectionsQuery.isLoading;

  const { filters, setSelectedSectionId } = useDashboardFilters();
  const divisions = useDivisionsMenuQuery();
  const root = divisions.data;

  useEffect(() => {
    if (filters.office && filters.selectedSectionId) {
      const selectedSection = allSections.find(
        (s) => s.id === filters.selectedSectionId
      );

      if (selectedSection) {
        const officeId = filters.office;
        const divisionPath = findDivisionPathByOfficeId(
          root?.tree?.children || [],
          officeId
        );

        const belongsToOffice =
          (selectedSection.division_id &&
            divisionPath?.includes(selectedSection.division_id)) ||
          (selectedSection.office_id && selectedSection.office_id === officeId);

        if (!belongsToOffice) {
          setSelectedSectionId(null);
        }
      }
    }
  }, [
    filters.office,
    filters.selectedSectionId,
    allSections,
    root?.tree?.children,
    setSelectedSectionId
  ]);

  const officeId = filters.office || currentOffice?.id;

  const divisionPath = findDivisionPathByOfficeId(
    root?.tree?.children || [],
    officeId
  );

  const primarySections = allSections
    ?.filter((section) => {
      return (
        section.parent_id === null &&
        ((section.division_id && divisionPath?.includes(section.division_id)) ||
          (section.office_id && section.office_id === officeId))
      );
    })
    .sort((a, b) => a.position - b.position);

  const selectedSection = filters.selectedSectionId
    ? allSections.find((s) => s.id === filters.selectedSectionId)
    : null;

  const subSections = selectedSection
    ? allSections.filter((s) => s.parent_id === selectedSection.id)
    : [];

  const data = usePagesQuery(
    selectedSection?.id ? { section_id: selectedSection.id } : undefined
  );

  const sectionPages = data?.length && data.length > 1 ? data : [];
  const pageLoaded = data?.length === 1 ? data[0] : null;

  const pagesLoading = !data;

  // Get the selected page
  const selectedPage = filters.selectedPageId
    ? sectionPages.find((p) => p.id === filters.selectedPageId)
    : null;

  // In "page view", related pages are all other pages in the same section
  const relatedPages = selectedPage
    ? sectionPages.filter((p) => p.id !== selectedPage.id)
    : [];

  const getSectionPath = (
    sectionId: string | null | undefined
  ): PageSection[] => {
    if (!sectionId) return [];
    const path: PageSection[] = [];
    let currentSection = allSections.find((s) => s.id === sectionId);
    while (currentSection) {
      path.unshift(currentSection);
      currentSection = allSections.find(
        (s) => s.id === currentSection?.parent_id
      );
    }
    return path;
  };

  const sectionPath = getSectionPath(selectedSection?.id);
  if (sectionsLoading) {
    return <PageInfoSkeleton />;
  }

  let content: React.ReactNode;
  // If a section ID is present in the state/URL
  if (filters.selectedSectionId) {
    if (!selectedSection) {
      content = (
        <div className="text-center">
          <h2 className="font-bold text-xl">Section not found</h2>
          <p className="mt-2 text-gray-600">
            The section you are looking for does not exist or you may not have
            permission to view it.
          </p>
        </div>
      );
    } else if (pagesLoading) {
      content = <LoadingSpinner />;
    } else if (selectedPage) {
      content = <PageView page={selectedPage} relatedPages={relatedPages} />;
    } else {
      content = (
        <SectionContent
          pages={sectionPages}
          section={selectedSection}
          subSections={subSections}
        />
      );
    }
  } else if (primarySections.length > 0) {
    // Show the main sections grid (home view)
    content = (
      <PageCardGrid>
        {primarySections.map((section) => (
          <SectionCard
            key={section.id}
            section={section}
            onClick={() => setSelectedSectionId(section.id)}
          />
        ))}
      </PageCardGrid>
    );
  } else {
    // No sections found, SiteInfo shouldn't be shown,
    // but just in case there is a message
    content = (
      <Box className="mt-8 rounded-lg border-2 border-gray-300 border-dashed p-12 text-center text-gray-500">
        <h3 className="font-medium text-xl">No pages found for your office</h3>
        <p className="mt-2">There is no info pages in this office.</p>
      </Box>
    );
  }
  return (
    <SiteInfoLayout
      section={selectedSection}
      page={selectedPage}
      sectionPath={sectionPath}
    >
      {content}
    </SiteInfoLayout>
  );
};
